import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CloudNavBar } from "../components/CloudNavBar";
import { toast } from "@ensafely/checker-ui";

interface PlatformRegionInfo {
  region: string;
  connected: boolean;
}

interface AvailableRegionsResponse {
  regions: PlatformRegionInfo[];
}

interface TenantRegionsResponse {
  regions: string[];
  quota: {
    current: number;
    limit: number;
  };
}

function ConnectedBadge({ connected }: { connected: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${
        connected
          ? "bg-green-100 text-green-800 border-green-200"
          : "bg-gray-100 text-gray-600 border-gray-200"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          connected ? "bg-green-500" : "bg-gray-400"
        }`}
      />
      {connected ? "Live" : "Offline"}
    </span>
  );
}

export default function PlatformRegionsPage() {
  const [available, setAvailable] = useState<PlatformRegionInfo[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [quota, setQuota] = useState<{ current: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    setError("");
    Promise.all([
      api.get<AvailableRegionsResponse>("/platform-regions/available"),
      api.get<TenantRegionsResponse>("/platform-regions"),
    ])
      .then(([availRes, tenantRes]) => {
        setAvailable(availRes.regions ?? []);
        setSelected(new Set(tenantRes.regions ?? []));
        setQuota(tenantRes.quota ?? null);
      })
      .catch((e) => {
        setError(e?.message || "Failed to load platform regions");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isAtLimit =
    quota !== null &&
    quota.limit > 0 &&
    selected.size >= quota.limit;

  const handleToggle = (region: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(region);
      } else {
        next.delete(region);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put<TenantRegionsResponse>("/platform-regions", {
        regions: Array.from(selected),
      });
      setSelected(new Set(res.regions ?? []));
      setQuota(res.quota ?? null);
      toast.success("Monitoring regions saved");
    } catch (e: any) {
      const msg = e?.message || "Failed to save regions";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const unlimited = quota !== null && quota.limit < 0;
  const usageBadge =
    quota !== null
      ? unlimited
        ? `${selected.size} / Unlimited`
        : `${selected.size} / ${quota.limit} used`
      : null;

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />
      <main id="main-content" className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Monitoring Regions</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Select which platform probe regions run your checks.
            </p>
          </div>
          {usageBadge && (
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-muted text-sm font-medium border">
              {usageBadge}
            </span>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading regions…</div>
        ) : available.length === 0 ? (
          <div className="rounded-lg border bg-card px-6 py-8 text-center text-sm text-muted-foreground">
            No platform probe regions are deployed yet.
          </div>
        ) : (
          <div className="rounded-lg border bg-card divide-y">
            {available.map((r) => {
              const isChecked = selected.has(r.region);
              const isDisabled = !isChecked && isAtLimit && !unlimited;
              return (
                <label
                  key={r.region}
                  className={`flex items-center justify-between px-5 py-4 ${
                    isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-muted/30"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={(e) => handleToggle(r.region, e.target.checked)}
                    />
                    <span className="font-medium uppercase text-sm tracking-wide">
                      {r.region}
                    </span>
                  </div>
                  <ConnectedBadge connected={r.connected} />
                </label>
              );
            })}
          </div>
        )}

        {isAtLimit && !unlimited && (
          <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            You&apos;ve reached your plan&apos;s region limit ({quota?.limit}).{" "}
            <Link
              to="/account/billing"
              className="font-medium underline hover:no-underline"
            >
              Upgrade your plan
            </Link>{" "}
            to add more regions.
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </main>
    </div>
  );
}
